/**
 * Testimonials carousel (logo & quote layout) - Alpine component.
 *
 * Tabbed carousel following the WAI-ARIA carousel pattern: avatar buttons form a tablist, the
 * quotes are tab panels, only the active panel is in the accessibility tree (hidden attribute).
 * Optional autoplay pauses on hover, on focus inside and while the tab is hidden, never runs when
 * the visitor prefers reduced motion, and can be toggled with a real button. Arrow keys on the
 * tablist move between testimonials.
 *
 * Reads: data-count (number of slides), data-autoplay (ms, 0 = off).
 */
document.addEventListener('alpine:init', () => {
    Alpine.data('testimonialsCarousel', () => ({
        active: 0,
        count: 1,
        interval: 0,
        playing: false,
        _timer: null,

        init() {
            const dataset = this.$el.dataset || {};
            this.count = Math.max(1, Number.parseInt(dataset.count, 10) || 1);
            this.interval = Math.max(0, Number.parseInt(dataset.autoplay, 10) || 0);

            const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (this.interval > 0 && this.count > 1 && !reducedMotion) {
                this.play();
            }

            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    this.stopTimer();
                } else if (this.playing) {
                    this.startTimer();
                }
            });
        },

        isActive(index) {
            return this.active === index;
        },

        go(index, focusTab = false) {
            this.active = (index + this.count) % this.count;
            if (focusTab) {
                this.$nextTick(() => {
                    const tab = this.$root.querySelector('.tcar-tab[data-index="' + this.active + '"]');
                    if (tab) {
                        tab.focus();
                    }
                });
            }
        },

        next(focusTab = false) {
            this.go(this.active + 1, focusTab);
        },

        prev(focusTab = false) {
            this.go(this.active - 1, focusTab);
        },

        /** User interaction stops autoplay for good - the visitor took over. */
        interact(callback) {
            this.pause();
            callback();
        },

        play() {
            this.playing = true;
            this.startTimer();
        },

        pause() {
            this.playing = false;
            this.stopTimer();
        },

        toggle() {
            this.playing ? this.pause() : this.play();
        },

        /** Hover / focus only suspend the timer; autoplay resumes afterwards. */
        suspend() {
            this.stopTimer();
        },

        resume() {
            if (this.playing) {
                this.startTimer();
            }
        },

        startTimer() {
            this.stopTimer();
            if (this.interval > 0) {
                this._timer = window.setInterval(() => this.next(), this.interval);
            }
        },

        stopTimer() {
            if (this._timer) {
                window.clearInterval(this._timer);
                this._timer = null;
            }
        },

        destroy() {
            this.stopTimer();
        },
    }));
});
